import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwm202Api, FM120, FM121, ValidationError } from '../../../services/pwm202Api';

export function usePWM202Record() {
  const [record, setRecord] = useState<FM120 | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (id: string): Promise<FM120 | null> => {
    setLoading(true);
    try {
      const data = await pwm202Api.get(id);
      setRecord(data);
      return data;
    } catch {
      message.error('載入庫位移轉單失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (
    formData: Partial<FM120>,
    details: Partial<FM121>[],
    isNew: boolean,
  ): Promise<{ success: boolean; result?: FM120; errors?: ValidationError[] }> => {
    setSaving(true);
    try {
      let result: FM120;
      if (isNew) {
        result = await pwm202Api.create(formData, details);
      } else {
        result = await pwm202Api.update(formData.fm120_01!, formData, details);
      }
      setRecord(result);
      message.success(isNew ? '新增成功' : '修改成功');
      return { success: true, result };
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { errors?: ValidationError[] } } };
      if (axiosErr.response?.status === 422) {
        const errors = axiosErr.response.data?.errors ?? [];
        message.error('資料驗證失敗，請確認後再試');
        return { success: false, errors };
      }
      message.error('儲存失敗，請稍後再試');
      return { success: false };
    } finally {
      setSaving(false);
    }
  }, []);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    try {
      await pwm202Api.delete(id);
      setRecord(null);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, []);

  return { record, loading, saving, load, save, remove, setRecord };
}
